// WorkoutModeButton.js - New component
import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dumbbell } from 'lucide-react';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Gradient button with rounded corners
const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: 30,
  padding: theme.spacing(1, 3),
  background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 80%)',
    boxShadow: '0 4px 12px rgba(255, 107, 0, 0.2)',
  }
}));

// Blurred Dialog with rounded corners
const BlurredDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : '0 10px 30px rgba(0, 0, 0, 0.15)',
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 30, 1)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
    border: theme.palette.actualMode === 'wallpaper'
      ? `1px solid ${WALLPAPER_COLORS.border}`
      : (theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.1)'),
  }
}));

const WorkoutModeButton = ({ onStartWorkout }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleStartWorkout = () => {
    setOpenDialog(false);
    if (onStartWorkout) {
      onStartWorkout();
    }
  };

  return (
    <>
      <GradientButton
        variant="contained"
        onClick={handleClickOpen}
        startIcon={!isMobile && <Dumbbell size={18} />}
      >
        {isMobile ? <Dumbbell size={18} /> : 'Workout Mode'}
      </GradientButton>

      <BlurredDialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="workout-mode-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="workout-mode-dialog-title" sx={{ textAlign: 'center', pt: 3 }}>
          Start Workout Mode
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Dumbbell size={48} color={theme.palette.mode === 'dark' ? '#FF6B00' : '#FF6B00'} />
          </Box>
          <Typography variant="body1" align="center">
            Are you ready to start your workout session? 
            Workout mode will help you track your exercises, sets, and rest times.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose} 
            sx={{ 
              color: theme.palette.text.secondary,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <GradientButton onClick={handleStartWorkout}>
            Start Workout
          </GradientButton>
        </DialogActions>
      </BlurredDialog>
    </>
  );
};

export default WorkoutModeButton;